import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  Divider,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import { MenuBook, CheckCircle } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { toggleWorkInstructionStep } from '../../../store/slices/operatorSlice';

const WorkInstructions: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { workInstructionSteps, activeOperation } = useSelector(
    (state: RootState) => state.operator
  );

  const completedCount = workInstructionSteps.filter((s) => s.isCompleted).length;
  const totalCount = workInstructionSteps.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleToggleStep = (stepId: string) => {
    dispatch(toggleWorkInstructionStep(stepId));
  };

  if (!activeOperation) {
    return (
      <Alert severity="info" icon={<MenuBook />}>
        Select a work order to view work instructions.
      </Alert>
    );
  }

  if (workInstructionSteps.length === 0) {
    return (
      <Alert severity="info">
        No work instructions available for this operation.
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Work Instructions</Typography>
          <Chip
            label={`${completedCount}/${totalCount} Steps`}
            color={completedCount === totalCount ? 'success' : 'default'}
            size="small"
            icon={completedCount === totalCount ? <CheckCircle /> : undefined}
          />
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressPercent}
          color={completedCount === totalCount ? 'success' : 'primary'}
          sx={{ height: 6, borderRadius: 3, mb: 3 }}
        />

        <Box display="flex" flexDirection="column" gap={0}>
          {workInstructionSteps.map((step, index) => (
            <Box key={step.id}>
              {index > 0 && <Divider />}
              <Box
                display="flex"
                alignItems="flex-start"
                gap={1}
                py={1.5}
                sx={{
                  opacity: step.isCompleted ? 0.6 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                <Checkbox
                  checked={step.isCompleted}
                  onChange={() => handleToggleStep(step.id)}
                  color="success"
                  sx={{ pt: 0.25, flexShrink: 0 }}
                />
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.25}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: step.isCompleted ? 'success.main' : 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {step.stepNumber}
                    </Box>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{
                        textDecoration: step.isCompleted ? 'line-through' : 'none',
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ pl: 4 }}
                  >
                    {step.description}
                  </Typography>
                  {step.imageUrl && (
                    <Box
                      sx={{
                        pl: 4,
                        mt: 1,
                        height: 120,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed',
                        borderColor: 'grey.300',
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        [Diagram / Image Placeholder]
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {completedCount === totalCount && (
          <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 2 }}>
            All work instruction steps completed!
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkInstructions;
